import {
  Cached as CachedIcon,
  FilterList,
  Search,
  SearchOff,
} from '@mui/icons-material';
import {
  Backdrop,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid2,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { getAudit } from '../../api/audit/getAudit';
import { useTranslationHook } from '../../hooks';

interface AuditItem {
  recordId: string;
  createdBy: string;
  createdOn: string;
  schemaName: string;
  tableName: string;
  operationType: string;
  originalValues: string;
  newValues: string;
}

const AuditTable: React.FC = () => {
  const { t } = useTranslationHook();
  const [data, setData] = useState<AuditItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState({
    tableName: '',
    operationType: '',
    accountId: '',
    userId: '',
    userName: '',
    startDate: '',
    endDate: '',
  });

  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isFilterOpen = Boolean(filterAnchorEl);

  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [jsonDialogTitle, setJsonDialogTitle] = useState('');
  const [jsonDialogContent, setJsonDialogContent] = useState<object | null>(
    null
  );

  const activeFilters = useMemo(() => {
    const list: { key: keyof typeof filters; label: string }[] = [];
    if (filters.accountId) list.push({ key: 'accountId', label: `${t('audit.filters.account-id')}: ${filters.accountId}` });
    if (filters.userId) list.push({ key: 'userId', label: `${t('audit.filters.user-id')}: ${filters.userId}` });
    if (filters.tableName) list.push({ key: 'tableName', label: `${t('audit.filters.table-name')}: ${filters.tableName}` });
    if (filters.operationType) list.push({ key: 'operationType', label: `${t('audit.filters.operation-type')}: ${filters.operationType}` });
    if (filters.startDate) list.push({ key: 'startDate', label: `${t('audit.filters.start-date')}: ${filters.startDate}` });
    if (filters.endDate) list.push({ key: 'endDate', label: `${t('audit.filters.end-date')}: ${filters.endDate}` });
    return list;
  }, [filters, t]);

  const handleOpenDialog = (title: string, jsonString: string) => {
    setJsonDialogTitle(title);

    try {
      setJsonDialogContent(JSON.parse(jsonString));
    } catch {
      setJsonDialogContent({ error: t('audit.error.invalid-json') });
    }

    setJsonDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setJsonDialogOpen(false);
    setJsonDialogContent(null);
  };

  const fetchData = async () => {
    setLoading(true);

    try {
      const operation = filters.operationType.toUpperCase();
      const allowedOps = ['INSERT', 'UPDATE'] as const;
      const typedOperation = operation as (typeof allowedOps)[number];
      const isValidOp = allowedOps.includes(typedOperation);

      const response = await getAudit({
        tableName: filters.tableName || undefined,
        operationType: isValidOp ? typedOperation : undefined,
        accountId: filters.accountId
          ? Number.parseInt(filters.accountId, 10)
          : undefined,
        userId: filters.userId ? Number.parseInt(filters.userId, 10) : undefined,
        userName: filters.userName || undefined,
        startDate: filters.startDate ? new Date(filters.startDate) : undefined,
        endDate: filters.endDate ? new Date(filters.endDate) : undefined,
        numRecords: rowsPerPage,
        offset: page * rowsPerPage,
      });

      setData(
        response.audit.map((item) => ({
          recordId: String(item.recordId),
          createdBy: String(item.createdBy),
          createdOn: item.createdOn,
          schemaName: item.schemaName,
          tableName: item.tableName,
          operationType: item.operationType,
          originalValues: JSON.stringify(item.originalValues),
          newValues: JSON.stringify(item.newValues),
        }))
      );
      setTotalCount(response.total);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchData();
    }, 500);

    return () => clearTimeout(handler);
  }, [filters, page, rowsPerPage]);

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(0);
  };

  const handleOperationTypeChange = (event: SelectChangeEvent<string>) => {
    setFilters((prev) => ({ ...prev, operationType: event.target.value }));
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleRows = data;

  return (
    <>
      <Backdrop
        open={loading}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', flex: 1 }}>
          <Toolbar>
            <Typography sx={{ flex: '1 1 100%' }} variant="h5">
              {t('audit.title')}
            </Typography>

            <Tooltip title={t('generic.filter')}>
              <IconButton onClick={(e) => setFilterAnchorEl(e.currentTarget)}>
                <Badge badgeContent={activeFilters.length} color="primary">
                  <FilterList color={isFilterOpen ? 'primary' : undefined} />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title={t('generic.refresh')}>
              <IconButton onClick={fetchData}>
                <CachedIcon />
              </IconButton>
            </Tooltip>
          </Toolbar>

          <TextField
            size="small"
            placeholder={t('audit.filters.user-name')}
            name="userName"
            value={filters.userName}
            onChange={handleFilterChange}
            sx={{ mb: 2 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              },
            }}
          />

          {activeFilters.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
              {activeFilters.map((f) => (
                <Chip
                  key={f.key}
                  label={f.label}
                  onDelete={() => {
                    setFilters({ ...filters, [f.key]: '' });
                  }}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Stack>
          )}

          <Popover
            open={isFilterOpen}
            anchorEl={filterAnchorEl}
            onClose={() => setFilterAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            slotProps={{ paper: { sx: { p: 3, width: 400 } } }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {t('generic.filter')}
              </Typography>

              <Grid2 container spacing={2}>
                <Grid2 size={12}>
                  <TextField
                    label={t("audit.filters.account-id")}
                    name="accountId"
                    value={filters.accountId}
                    onChange={handleFilterChange}
                    fullWidth
                    size="small"
                  />
                </Grid2>
                <Grid2 size={12}>
                  <TextField
                    label={t('audit.filters.user-id')}
                    name="userId"
                    value={filters.userId}
                    onChange={handleFilterChange}
                    fullWidth
                    size="small"
                  />
                </Grid2>
                <Grid2 size={12}>
                  <TextField
                    label={t('audit.filters.table-name')}
                    name="tableName"
                    value={filters.tableName}
                    onChange={handleFilterChange}
                    fullWidth
                    size="small"
                  />
                </Grid2>
                <Grid2 size={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t("audit.filters.operation-type")}</InputLabel>
                    <Select
                      name="operationType"
                      value={filters.operationType}
                      onChange={handleOperationTypeChange}
                      label={t("audit.filters.operation-type")}
                    >
                      <MenuItem value=""><em>{t('generic.none')}</em></MenuItem>
                      <MenuItem value="INSERT">INSERT</MenuItem>
                      <MenuItem value="UPDATE">UPDATE</MenuItem>
                    </Select>
                  </FormControl>
                </Grid2>
                <Grid2 size={6}>
                  <DatePicker
                    label={t("audit.filters.start-date")}
                    value={filters.startDate ? moment(filters.startDate) : null}
                    onChange={(newValue) =>
                      setFilters((prev) => ({
                        ...prev,
                        startDate: newValue?.format('YYYY-MM-DD') || '',
                      }))
                    }
                    format="DD/MM/YYYY"
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Grid2>
                <Grid2 size={6}>
                  <DatePicker
                    label={t("audit.filters.end-date")}
                    value={filters.endDate ? moment(filters.endDate) : null}
                    onChange={(newValue) =>
                      setFilters((prev) => ({
                        ...prev,
                        endDate: newValue?.format('YYYY-MM-DD') || '',
                      }))
                    }
                    format="DD/MM/YYYY"
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Grid2>
              </Grid2>
            </Box>
          </Popover>

          <TableContainer sx={{ flex: 1 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("audit.table.record-id")}</TableCell>
                  <TableCell>{t("audit.table.created-by")}</TableCell>
                  <TableCell>{t("audit.table.created-on")}</TableCell>
                  <TableCell>{t("audit.table.schema-name")}</TableCell>
                  <TableCell>{t("audit.table.table-name")}</TableCell>
                  <TableCell>{t("audit.table.operation-type")}</TableCell>
                  <TableCell>{t("audit.table.original-values")}</TableCell>
                  <TableCell>{t("audit.table.new-values")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 10 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: 0.6 }}>
                        <SearchOff sx={{ fontSize: 64, mb: 2 }} />
                        <Typography variant="h6">{t('audit.table.no-records')}</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleRows.map((item, index) => (
                    <TableRow hover key={`${item.recordId}-${index}`}>
                      <TableCell>{item.recordId}</TableCell>
                      <TableCell>{item.createdBy}</TableCell>
                      <TableCell>
                        {new Intl.DateTimeFormat('pt-PT').format(new Date(item.createdOn))}
                      </TableCell>
                      <TableCell>{item.schemaName}</TableCell>
                      <TableCell>{item.tableName}</TableCell>
                      <TableCell>{item.operationType}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => handleOpenDialog(t('audit.table.original-values'), item.originalValues)}
                        >
                          {t('generic.view-details')}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => handleOpenDialog(t('audit.table.new-values'), item.newValues)}
                        >
                          {t('generic.view-details')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>

        <Dialog
          open={jsonDialogOpen}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{jsonDialogTitle}</DialogTitle>
          <DialogContent dividers>
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace' }}>
              {JSON.stringify(jsonDialogContent, null, 2)}
            </pre>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} variant="contained">
              {t('generic.close')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default AuditTable;
